import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  Paper,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { fetchOrderDetails } from "../../../../../services/pos/Tables";

const statusColors = {
  pending: "#ff9800",
  preparing: "#2196f3",
  ready: "#4caf50",
  delivered: "#9e9e9e",
  cancelled: "#f44336",
};

const statusLabels = {
  pending: "في التجهيز",
  preparing: "قيد التحضير",
  ready: "جاهز",
  delivered: "تم التسليم",
  cancelled: "ملغي",
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const OrderItemsDialog = ({ open, onClose, orderId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && orderId) {
      loadOrderDetails();
    }
  }, [open, orderId]);

  const loadOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrderDetails(orderId);
      setOrderDetails(data);
    } catch (err) {
      console.error("Failed to load order details:", err);
      setError(err.message || "فشل في تحميل تفاصيل الأوردر");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${process.env.REACT_APP_API_BASE_URL || ""}/${imagePath}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "md"}
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          /* more vertical space on desktop/tablet */
          maxHeight: isMobile ? "100vh" : "95vh",
          m: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: isMobile ? 1 : 1.5,
          px: isMobile ? 2 : 3,
          pt: isMobile ? 2 : 2.5,
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RestaurantMenuIcon
            color="primary"
            sx={{ fontSize: isMobile ? 22 : 24 }}
          />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            fontWeight={700}
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            تفاصيل الأوردر
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size={isMobile ? "medium" : "small"}
          sx={{
            bgcolor: "rgba(0,0,0,0.05)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          /* tighter padding + internal scrolling */
          p: isMobile ? 0.6 : isTablet ? 0.9 : 1.0,
          overflowX: "hidden",
          overflowY: "auto",
          maxHeight: isMobile ? "calc(100vh - 120px)" : "calc(95vh - 140px)",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error" variant="h6">
              {error}
            </Typography>
            <Button
              onClick={loadOrderDetails}
              variant="outlined"
              sx={{ mt: 2 }}
            >
              إعادة المحاولة
            </Button>
          </Box>
        ) : orderDetails ? (
          <Box>
            {/* معلومات الأوردر الأساسية */}
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 0.6 : 0.9,
                bgcolor: "rgba(25, 118, 210, 0.08)",
                borderRadius: isMobile ? 1.5 : 2,
                mb: 2,
              }}
            >
              <Grid container spacing={isMobile ? 1 : 1.2}>
                {/* رقم الأوردر */}
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                    >
                      رقم الأوردر:
                    </Typography>
                    <Chip
                      label={`#${orderDetails.daily_increment}`}
                      size={isMobile ? "small" : "small"}
                      color="primary"
                      sx={{
                        height: isMobile ? 20 : 24,
                        fontSize: isMobile ? "10px" : "12px",
                        "& .MuiChip-label": { px: isMobile ? 0.8 : 1 },
                      }}
                    />
                  </Box>
                </Grid>

                {/* وقت الفتح */}
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon
                      fontSize="small"
                      color="action"
                      sx={{ fontSize: isMobile ? 16 : 18 }}
                    />
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      fontWeight={600}
                      sx={{ fontSize: isMobile ? "11px" : "inherit" }}
                    >
                      {formatTime(orderDetails.open_time)}
                    </Typography>
                  </Box>
                </Grid>

                {/* الحالة */}
                <Grid item xs={12} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                    >
                      الحالة:
                    </Typography>
                    <Chip
                      label={
                        statusLabels[orderDetails.status] ||
                        orderDetails.status
                      }
                      size={isMobile ? "small" : "small"}
                      sx={{
                        bgcolor: statusColors[orderDetails.status] || "#999",
                        color: "#fff",
                        fontWeight: 600,
                        height: isMobile ? 20 : 24,
                        fontSize: isMobile ? "10px" : "12px",
                        "& .MuiChip-label": { px: isMobile ? 0.8 : 1 },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* بيانات العميل - قسم منفصل */}
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 0.6 : 0.9,
                bgcolor: "rgba(76, 175, 80, 0.08)",
                borderRadius: isMobile ? 1.5 : 2,
                mb: 2,
              }}
            >
              <Typography
                variant={isMobile ? "body2" : "subtitle1"}
                fontWeight={700}
                sx={{ mb: isMobile ? 1.5 : 2 }}
              >
                بيانات العميل
              </Typography>

              <Grid container spacing={isMobile ? 1 : 1.2}>
                {/* اسم العميل */}
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon
                      fontSize="small"
                      color="action"
                      sx={{ fontSize: isMobile ? 16 : 18 }}
                    />
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                    >
                      الاسم:
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      fontWeight={600}
                      sx={{
                        fontSize: isMobile ? "11px" : "inherit",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {orderDetails.customer?.name || "-"}
                    </Typography>
                  </Box>
                </Grid>

                {/* البريد الإلكتروني */}
                {orderDetails.customer?.email && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18 }}
                      />
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                      >
                        البريد:
                      </Typography>
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        fontWeight={600}
                        sx={{
                          fontSize: isMobile ? "11px" : "inherit",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {orderDetails.customer.email}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* هاتف 1 */}
                {orderDetails.customer?.phone1 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18 }}
                      />
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                      >
                        هاتف 1:
                      </Typography>
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        fontWeight={600}
                        sx={{
                          fontSize: isMobile ? "11px" : "inherit",
                          direction: "ltr",
                          textAlign: "left",
                          flex: 1,
                        }}
                      >
                        {orderDetails.customer.phone1}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* هاتف 2 */}
                {orderDetails.customer?.phone2 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18 }}
                      />
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                      >
                        هاتف 2:
                      </Typography>
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        fontWeight={600}
                        sx={{
                          fontSize: isMobile ? "11px" : "inherit",
                          direction: "ltr",
                          textAlign: "left",
                          flex: 1,
                        }}
                      >
                        {orderDetails.customer.phone2}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* هاتف 3 */}
                {orderDetails.customer?.phone3 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18 }}
                      />
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                      >
                        هاتف 3:
                      </Typography>
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        fontWeight={600}
                        sx={{
                          fontSize: isMobile ? "11px" : "inherit",
                          direction: "ltr",
                          textAlign: "left",
                          flex: 1,
                        }}
                      >
                        {orderDetails.customer.phone3}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* عنوان 1 */}
                {orderDetails.customer?.address1 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <LocationOnIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18, mt: 0.3 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                        >
                          عنوان 1:
                        </Typography>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          fontWeight={600}
                          sx={{
                            fontSize: isMobile ? "11px" : "inherit",
                            wordBreak: "break-word",
                          }}
                        >
                          {orderDetails.customer.address1}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* عنوان 2 */}
                {orderDetails.customer?.address2 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <LocationOnIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18, mt: 0.3 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                        >
                          عنوان 2:
                        </Typography>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          fontWeight={600}
                          sx={{
                            fontSize: isMobile ? "11px" : "inherit",
                            wordBreak: "break-word",
                          }}
                        >
                          {orderDetails.customer.address2}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* عنوان 3 */}
                {orderDetails.customer?.address3 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <LocationOnIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: isMobile ? 16 : 18, mt: 0.3 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? "10px" : "inherit" }}
                        >
                          عنوان 3:
                        </Typography>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          fontWeight={600}
                          sx={{
                            fontSize: isMobile ? "11px" : "inherit",
                            wordBreak: "break-word",
                          }}
                        >
                          {orderDetails.customer.address3}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* رسالة في حالة عدم وجود بيانات */}
                {!orderDetails.customer?.name && 
                 !orderDetails.customer?.email && 
                 !orderDetails.customer?.phone1 && 
                 !orderDetails.customer?.phone2 && 
                 !orderDetails.customer?.phone3 && 
                 !orderDetails.customer?.address1 && 
                 !orderDetails.customer?.address2 && 
                 !orderDetails.customer?.address3 && (
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 2 }}
                    >
                      لا توجد بيانات متاحة للعميل
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* قائمة الأصناف */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant={isMobile ? "body2" : "subtitle1"}
                fontWeight={700}
                sx={{ mb: isMobile ? 1 : 1.2 }}
              >
                الأصناف ({orderDetails.items?.length || 0})
              </Typography>

              {orderDetails.items && orderDetails.items.length > 0 ? (
                /* scrollable list so more items fit on screen */
                <List sx={{ p: 0, maxHeight: isMobile ? "calc(100vh - 300px)" : "calc(95vh - 360px)", overflowY: "auto" }}>
                  {orderDetails.items.map((item, index) => (
                    <Paper
                      key={item.id}
                      elevation={isMobile ? 0 : 1}
                      sx={{
                        /* tighter item spacing */
                        mb: isMobile ? 0.4 : 0.6,
                        p: isMobile ? 0.45 : 0.7,
                        borderRadius: isMobile ? 0.8 : 1.2,
                        transition: "all 0.2s",
                        border: isMobile ? "1px solid #e0e0e0" : "none",
                        "&:hover": {
                          boxShadow: isMobile ? 1 : 3,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: isMobile ? 1 : 1.2,
                          /* force row layout to reduce vertical space */
                          flexDirection: "row",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {/* صورة المنتج */}
                        <Avatar
                          src={getImageUrl(item.product_image)}
                          alt={item.product_name}
                          variant="rounded"
                          sx={{
                            /* smaller avatar for denser rows */
                            width: isMobile ? 36 : 44,
                            height: isMobile ? 36 : 44,
                            border: "1px solid #e0e0e0",
                            flexShrink: 0,
                          }}
                        >
                          <RestaurantMenuIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
                        </Avatar>

                        {/* تفاصيل المنتج */}
                        <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: isMobile ? 0.3 : 0.6,
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant={isMobile ? "body2" : "body2"}
                                fontWeight={700}
                                sx={{
                                  fontSize: isMobile ? "11px" : "12px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {item.product_name}
                              </Typography>
                              {item.note && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", fontSize: isMobile ? "9px" : "10px", display: "block", mt: 0.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  ملاحظة: {item.note}
                                </Typography>
                              )}
                            </Box>

                            <Chip
                              label={statusLabels[item.status] || item.status}
                              size="small"
                              sx={{
                                bgcolor: statusColors[item.status] || "#999",
                                color: "#fff",
                                fontSize: isMobile ? "8px" : "9px",
                                height: isMobile ? 16 : 18,
                                flexShrink: 0,
                                "& .MuiChip-label": { px: isMobile ? 0.5 : 0.6 },
                              }}
                            />
                          </Box>

                          <Grid container spacing={isMobile ? 0.5 : 1} sx={{ mt: 0.3, alignItems: 'center' }}>
                            <Grid item xs={4}>
                              <Typography
                                variant={isMobile ? "caption" : "body2"}
                                color="text.secondary"
                                sx={{ fontSize: isMobile ? "9px" : "10px" }}
                              >
                                الكمية:
                              </Typography>
                              <Typography
                                variant={isMobile ? "body2" : "body1"}
                                fontWeight={600}
                                sx={{ fontSize: isMobile ? "11px" : "12px" }}
                              >
                                {item.quantity}
                              </Typography>
                            </Grid>

                            <Grid item xs={4}>
                              <Typography
                                variant={isMobile ? "caption" : "body2"}
                                color="text.secondary"
                                sx={{ fontSize: isMobile ? "9px" : "10px" }}
                              >
                                السعر:
                              </Typography>
                              <Typography
                                variant={isMobile ? "body2" : "body1"}
                                fontWeight={600}
                                sx={{ fontSize: isMobile ? "11px" : "12px" }}
                              >
                                {item.price} ج.م
                              </Typography>
                            </Grid>

                            <Grid item xs={4}>
                              <Typography
                                variant={isMobile ? "caption" : "body2"}
                                color="text.secondary"
                                sx={{ fontSize: isMobile ? "9px" : "10px" }}
                              >
                                الإجمالي:
                              </Typography>
                              <Typography
                                variant={isMobile ? "body2" : "body1"}
                                fontWeight={700}
                                color="primary"
                                sx={{ fontSize: isMobile ? "11px" : "12px" }}
                              >
                                {item.total} ج.م
                              </Typography>
                            </Grid>
                          </Grid>

                          {item.open_time && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: isMobile ? 0.8 : 1,
                                display: "block",
                                fontSize: isMobile ? "10px" : "12px",
                              }}
                            >
                              وقت الإضافة: {formatTime(item.open_time)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </List>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    bgcolor: "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                  }}
                >
                  <RestaurantMenuIcon
                    sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    لا توجد أصناف في هذا الأوردر
                  </Typography>
                </Box>
              )}
            </Box>

            {/* إجمالي الأوردر */}
            <Paper
              elevation={isMobile ? 1 : 2}
              sx={{
                p: isMobile ? 0.6 : 0.9,
                bgcolor: "rgba(76, 175, 80, 0.1)",
                borderRadius: isMobile ? 1.5 : 2,
                border: "2px solid rgba(76, 175, 80, 0.3)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: 1,
                }}
              >
                <Typography variant={isMobile ? "body1" : "h6"} fontWeight={700}>
                  الإجمالي الكلي:
                </Typography>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight={700}
                  color="success.main"
                >
                  {orderDetails.total} ج.م
                </Typography>
              </Box>
              {orderDetails.pending_items_count > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: "block",
                    fontSize: isMobile ? "10px" : "12px",
                  }}
                >
                  عدد الأصناف المعلقة: {orderDetails.pending_items_count}
                </Typography>
              )}
            </Paper>
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: isMobile ? 0.8 : 1,
          position: isMobile ? "sticky" : "relative",
          bottom: 0,
          bgcolor: "background.paper",
          boxShadow: isMobile ? "0 -2px 4px rgba(0,0,0,0.1)" : "none",
        }}
      >
        <Button
          onClick={onClose}
          variant={isMobile ? "contained" : "outlined"}
          color="secondary"
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderItemsDialog;
